import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Webhook, Save } from "lucide-react";

/**
 * CMS integration settings — the webhook we POST to start a push job, the
 * endpoint we poll for its status, and the optional API key sent as
 * X-API-Key on both calls. See docs/developer/09-integrations.md for the
 * exact request/response contract.
 *
 * The user-sync webhook field is commented out as of 2026-09-24: the CMS now
 * builds its own users from Keycloak's roles and groups at login, so nothing
 * reads cms_user_webhook_url any more (see the "DISABLED 2026-09-24" blocks
 * in backend/user_provisioning.py). Leaving an editable field that drives
 * nothing would be worse than hiding it. The stored setting is left in place
 * untouched, so re-enabling is just uncommenting.
 */
export function CmsIntegrationCard() {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [statusUrl, setStatusUrl] = useState("");
  // const [userWebhookUrl, setUserWebhookUrl] = useState("");  // CMS user sync — disabled 2026-09-24
  const [hostnameRenameWebhookUrl, setHostnameRenameWebhookUrl] = useState("");
  const [siteAssignWebhookUrl, setSiteAssignWebhookUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  // Write-only: the API reports whether a key is saved, never its value.
  const [apiKeySet, setApiKeySet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings");
        const map = Object.fromEntries(data.map((s) => [s.key, s.value]));
        setWebhookUrl(map.cms_webhook_url || "");
        setStatusUrl(map.cms_status_url || "");
        // setUserWebhookUrl(map.cms_user_webhook_url || "");  // disabled 2026-09-24
        setHostnameRenameWebhookUrl(map.cms_hostname_rename_webhook_url || "");
        setSiteAssignWebhookUrl(map.cms_site_assign_webhook_url || "");
        setApiKey(map.cms_api_key || "");
        setApiKeySet(!!data.find((s) => s.key === "cms_api_key")?.is_set);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        client.put("/settings/cms_webhook_url", { value: webhookUrl.trim() }),
        client.put("/settings/cms_status_url", { value: statusUrl.trim() }),
        // client.put("/settings/cms_user_webhook_url", { value: userWebhookUrl.trim() }),  // disabled 2026-09-24
        client.put("/settings/cms_hostname_rename_webhook_url", { value: hostnameRenameWebhookUrl.trim() }),
        client.put("/settings/cms_site_assign_webhook_url", { value: siteAssignWebhookUrl.trim() }),
        client.put("/settings/cms_api_key", { value: apiKey.trim() }),
      ]);
      toast.success("CMS integration settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="settings-cms-card">
      <div className="mb-1 flex items-center gap-2">
        <Webhook className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">CMS Integration</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        The push/status webhooks are used by the &quot;Push to CMS&quot; action on Activations — the webhook starts an
        import job on the CMS side, the status URL is polled every 15s until it finishes. User accounts are no longer
        pushed here: the CMS reads each user&apos;s roles and site groups from Keycloak when they log in.
      </p>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label className="data-label mb-1 block">Push webhook URL</Label>
          <Input
            data-testid="settings-cms-webhook-url"
            placeholder="https://cms.example.com/hooks/activation-push"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            disabled={loading || saving}
            className="font-mono text-xs"
            autoComplete="off"
          />
        </div>
        <div>
          <Label className="data-label mb-1 block">Status URL</Label>
          <Input
            data-testid="settings-cms-status-url"
            placeholder="https://cms.example.com/hooks/activation-status"
            value={statusUrl}
            onChange={(e) => setStatusUrl(e.target.value)}
            disabled={loading || saving}
            className="font-mono text-xs"
            autoComplete="off"
          />
        </div>
        <div>
          <Label className="data-label mb-1 block">Hostname rename webhook URL</Label>
          <Input
            data-testid="settings-cms-hostname-rename-webhook-url"
            placeholder="https://cms.example.com/hooks/hostname-rename"
            value={hostnameRenameWebhookUrl}
            onChange={(e) => setHostnameRenameWebhookUrl(e.target.value)}
            disabled={loading || saving}
            className="font-mono text-xs"
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Called when a dealer with a live CMS site renames their website host, to kick off the CMS-side rename;
            the CMS reports the outcome back via POST /api/external/host-rename/webhook.
          </p>
        </div>
        <div>
          <Label className="data-label mb-1 block">Assign existing website webhook URL</Label>
          <Input
            data-testid="settings-cms-site-assign-webhook-url"
            placeholder="https://cms.example.com/hooks/site-assign"
            value={siteAssignWebhookUrl}
            onChange={(e) => setSiteAssignWebhookUrl(e.target.value)}
            disabled={loading || saving}
            className="font-mono text-xs"
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Called when a System Administrator maps a dealer onto an already-existing CMS site by its global ID;
            the CMS reports the outcome back via POST /api/external/site-assign/webhook.
          </p>
        </div>
        {/* DISABLED 2026-09-24 — the CMS derives its users from Keycloak at login.
        <div>
          <Label className="data-label mb-1 block">User sync webhook URL</Label>
          <Input
            data-testid="settings-cms-user-webhook-url"
            placeholder="https://cms.example.com/hooks/user-sync"
            value={userWebhookUrl}
            onChange={(e) => setUserWebhookUrl(e.target.value)}
            disabled={loading || saving}
            className="font-mono text-xs"
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Called on every user create/update/deactivate/restore, so the CMS can keep its own mapped user in sync.
          </p>
        </div>
        */}
        <div>
          <Label className="data-label mb-1 block">API key (optional)</Label>
          <Input
            data-testid="settings-cms-api-key"
            type="password"
            placeholder={apiKeySet && !apiKey ? "•••••••• saved — leave blank to keep" : "Sent as X-API-Key on both calls"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={loading || saving}
            className="font-mono text-xs"
            autoComplete="new-password"
          />
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Button data-testid="settings-cms-save" onClick={save} disabled={loading || saving} className="gap-1.5">
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </div>
    </Card>
  );
}
